/**
 * O painel de emoji do editor (H16c).
 *
 * ── Fechar é do painel, e não de quem o abre ──────────────────────────────
 *
 * Um painel que só fecha pelo próprio botão vira um painel que fica aberto: a
 * pessoa clica fora, nada acontece, e ela clica de novo no botão para descobrir
 * como sair. Aqui `Esc` e o clique fora fecham — que é o que qualquer menu faz,
 * e o que o dedo já espera.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { GRUPOS_DE_EMOJI, lerRecentes, registrarUso } from '../lib/emojis';

export function SeletorDeEmoji({
  aoEscolher,
  aoFechar,
}: {
  aoEscolher: (emoji: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const [recentes, definirRecentes] = useState<readonly string[]>(() => lerRecentes());
  const painel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') aoFechar();
    }
    function aoClicar(evento: MouseEvent): void {
      if (!painel.current?.contains(evento.target as Node)) aoFechar();
    }

    document.addEventListener('keydown', aoTeclar);
    /*
     * Na CAPTURA, e não na propagação.
     *
     * O botão que abre o painel também é "fora" dele. Na propagação, o clique
     * que abre chegaria aqui logo depois e fecharia na mesma hora — o painel
     * pisca e some, e parece que o botão não funciona.
     *
     * Capturando, o ouvinte só existe a partir do render seguinte, e o clique
     * que abriu já terminou.
     */
    document.addEventListener('mousedown', aoClicar, true);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('mousedown', aoClicar, true);
    };
  }, [aoFechar]);

  function escolher(emoji: string): void {
    definirRecentes(registrarUso(emoji));
    aoEscolher(emoji);
  }

  return (
    <div className="emojis" ref={painel} role="dialog" aria-label="Escolher emoji">
      {recentes.length > 0 && (
        <section className="emojis__grupo">
          <h4 className="emojis__titulo">Recentes</h4>
          <Fileira itens={recentes} aoEscolher={escolher} />
        </section>
      )}

      {GRUPOS_DE_EMOJI.map((grupo) => (
        <section className="emojis__grupo" key={grupo.nome}>
          <h4 className="emojis__titulo">{grupo.nome}</h4>
          <Fileira itens={grupo.itens} aoEscolher={escolher} />
        </section>
      ))}
    </div>
  );
}

function Fileira({
  itens,
  aoEscolher,
}: {
  itens: readonly string[];
  aoEscolher: (emoji: string) => void;
}): ReactNode {
  return (
    <div className="emojis__fileira">
      {itens.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className="emojis__item"
          /*
           * `onMouseDown` com `preventDefault`, e não só `onClick`.
           *
           * Clicar num botão tira o foco do campo de texto, e com o foco vai a
           * seleção — `selectionStart` volta a zero e o emoji seria inserido no
           * começo do texto. Impedir o padrão do `mousedown` mantém o cursor
           * onde ele estava.
           */
          onMouseDown={(evento) => evento.preventDefault()}
          onClick={() => aoEscolher(emoji)}
          aria-label={`Inserir ${emoji}`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}
